/**
 * This store is not needed at all if this ticket is done. This is ugly workaround because ak/inline-dialog does not support this
 * The below is the request of the new feature we need
 * https://ecosystem.atlassian.net/projects/DS/queues/issue/DS-6729
 * When this is done, we will remove this ugly store and use the natural behavior of ak/inline-dialog
 * https://hello.atlassian.net/browse/UFC-138
 */

import { createStore, type Action, createHook } from '@atlassian/react-sweet-state';
import type { State } from './types';

/**
 * Actions
 * =======
 */

/**
 * Close modal
 */
export const closeModal =
	(): Action<State> =>
	({ getState, setState }) => {
		const { isOpen } = getState();
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const newIsOpen: Record<string, any> = {};

		Object.keys(isOpen).forEach((id) => {
			newIsOpen[id] = false;
		});

		setState({ isOpen: newIsOpen });
	};

/**
 * Open modal
 */
export const openModal =
	(targetId: string): Action<State> =>
	({ getState, setState }) => {
		const { isOpen } = getState();
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const newIsOpen: Record<string, any> = {};

		Object.keys(isOpen).forEach((id) => {
			if (id === targetId) {
				newIsOpen[id] = true;
				return;
			}

			newIsOpen[id] = false;
		});

		setState({ isOpen: newIsOpen });
	};

/**
 * Register
 */
export const register =
	(id: string): Action<State> =>
	({ getState, setState }) => {
		const { isOpen } = getState();
		const newIsOpen = { ...isOpen };
		newIsOpen[id] = false;

		setState({ isOpen: newIsOpen });
	};

/**
 * Unregister
 */
export const unregister =
	(id: string): Action<State> =>
	({ getState, setState }) => {
		const { isOpen } = getState();
		const newIsOpen = { ...isOpen };
		delete newIsOpen[id];

		setState({ isOpen: newIsOpen });
	};

export const actions = {
	closeModal,
	openModal,
	register,
	unregister,
} as const;

export type Actions = typeof actions;

/**
 * Initial state
 */
export const initialState: State = {
	isOpen: {},
};

/**
 * Instance
 */
const Store = createStore<State, Actions>({
	name: 'inlineDialogStore',
	initialState,
	actions,
});

export const useInlineDialog = createHook(Store);
